import { makeObservable, observable, runInAction } from 'mobx';
import { BaseStore } from './baseStore';
import { RootStore } from './rootStore';
import { UserStore } from './userStore';
import { ApiError, PaymentService } from '/src/api';
import { Payment, TLoanId, TPaymentId } from '/src/lib/models';
import {
  CreatePaymentForm,
  ISODateTimeString,
  MenuItemType,
  TCreatePaymentRequest,
  TListPaymentsRequest,
  UserRole,
} from '/src/lib/types';

export interface PaymentStoreProps {
  /**
   * List of Payments
   */
  payments?: Payment[];
  /**
   * Currently selected Payment
   */
  currentPayment?: Payment;
  /**
   * Estimated payoff amount
   */
  payoffEstimate?: number;
  /**
   * Set to true when the API generates payoff estimates
   */
  isEstimating?: boolean;
}

export class PaymentStore extends BaseStore implements PaymentStoreProps {
  private didInitMenu = false;
  userStore: UserStore;

  payments?: Payment[];
  currentPayment?: Payment;
  payoffEstimate?: number;
  isEstimating?: boolean;

  constructor(rootStore: RootStore) {
    super(rootStore);

    this.userStore = rootStore.userStore;

    makeObservable(this, {
      payments: observable,
      currentPayment: observable,
      payoffEstimate: observable,
      isEstimating: observable,
    });
  }

  /**
   * Adds Payment nav menu items to the app header.
   */
  initMenuItems = () => {
    if (this.didInitMenu || !this.rootStore.userStore.user) return;

    // Add menu items
    this.rootStore.menuStore.addMenuItem(
      'topbar',
      'Payments',
      'payments',
      this.rootStore.userStore.isLender ? MenuItemType.Item : MenuItemType.Dropdown,
      '/payments(/create)?',
      false,
      undefined,
      3
    );
    if (!this.rootStore.userStore.isLender) {
      this.rootStore.menuStore.addSubMenuItem('topbar', 'payments', 'List Payments', 'payments');
      this.rootStore.menuStore.addSubMenuItem(
        'topbar',
        'payments',
        'New Payment',
        'payments/create',
        'payments/create',
        false,
        [UserRole.Admin, UserRole.Company]
      );
    }

    runInAction(() => (this.didInitMenu = true));
  };

  /**** Data Processing ****/

  /**
   * Formats List Payments responses.
   *
   * @param payments - An array of Payments to format
   * @returns The formatted Payments
   */
  formatPaymentList = (payments: Payment[]) => payments.map((payment) => this.formatPayment(payment));

  /**
   * Format Get Payment responses.
   *
   * @param payment - The Payment to format
   * @returns The formatted Payment
   */
  formatPayment = (payment: Payment) => {
    // Parse
    if (payment.datePaid) payment.datePaid = new Date(payment.datePaid as unknown as ISODateTimeString);
    if (payment.dateDeposited) payment.dateDeposited = new Date(payment.dateDeposited as unknown as ISODateTimeString);

    // Return
    return payment;
  };

  /**** Payment CRUD ****/

  /**
   * Estimates a Loan's total payoff amount.
   *
   * @param loanId - ID of the Loan to get payoff amount for
   * @param asOfDate - Gets payoff estimate as of a given date
   */
  estimatePayoff = async (loanId: TLoanId, asOfDate: Date) => {
    const response = await PaymentService.estimatePayoff({ loanId, asOfDate });

    runInAction(() => {
      this.payoffEstimate = undefined;
      this.isEstimating = true;
    });

    let attempts = 0;
    const maxAttempts = 5;
    let backoff = 1000; // in ms

    const getReport = async () => {
      try {
        const report = await PaymentService.getPayoffReport(response._id);

        runInAction(() => {
          this.payoffEstimate = report?.payoffEstimate;
          this.isEstimating = false;
        });
      } catch (err) {
        // Poll using exponential backoff
        if ((err as ApiError).status === 400) {
          attempts += 1;

          if (attempts > maxAttempts) {
            runInAction(() => (this.isEstimating = false));
          } else {
            setTimeout(getReport, backoff);
            backoff *= 2;
          }
        }
      }
    };

    getReport();
  };

  /**
   * Fetches a list of Payments.
   *
   * @param query - Optional query to send with the API request
   * @returns An array of Payments
   */
  query = async (query?: TListPaymentsRequest) => {
    // if (query) query.px = true;
    const response = await PaymentService.query(query);

    runInAction(() => (this.payments = this.formatPaymentList(response)));

    return this.payments;
  };

  /**
   * Fetches a single Payment and sets it as the current Payment.
   *
   * @param paymentId - ID of the Payment to fetch
   * @returns A Payment object
   */
  get = async (paymentId: TPaymentId) => {
    const response = await PaymentService.get(paymentId);

    runInAction(() => (this.currentPayment = this.formatPayment(response)));

    return this.currentPayment;
  };

  /**
   * Creates an Payment.
   *
   * @param formValues - A populated CreatePaymentForm object
   * @returns The new Payment
   */
  create = async (formValues: CreatePaymentForm) => {
    if (!this.userStore.user) throw new Error('Not authorized');

    // Create new Payment object
    const payment: TCreatePaymentRequest = {
      name: formValues.name,
      assetId: formValues.asset._id,
      loan: formValues.loan._id,
      client: formValues.asset.client,
      company: formValues.company._id,
      amount: formValues.amount,
      paymentType: formValues.paymentType,
      datePaid: formValues.datePaid,
      dateDeposited: formValues.dateDeposited,
      notes: formValues.notes,
      effectiveDate: formValues.dateDeposited,
      files: formValues.files,
    };

    const response = await PaymentService.create(payment);

    return response;
  };

  /**
   * Updates an Payment.
   *
   * @param payment - The Payment to update
   * @returns The updated Payment
   */
  update = async (payment: Payment) => {
    const response = await PaymentService.update(payment._id, payment);

    runInAction(() => (this.currentPayment = Object.assign(this.currentPayment ?? {}, response)));

    return this.currentPayment;
  };

  /**
   * Removes a Payment.
   *
   * @param paymentId - ID of the Payment to remove
   * @returns The remove Payment
   */
  remove = async (paymentId: TPaymentId) => {
    return await PaymentService.remove(paymentId);
  };
}
